import { supabase } from '../lib/supabase';

const viewingHistoryService = {
    /**
     * Track a vendor view in the database
     * @param {string} userId - The user's ID
     * @param {string} vendorId - The vendor's ID
     * @returns {Promise<{success: boolean, data?: any, error?: string}>}
     */
    async trackView(userId, vendorId) {
        try {
            if (!userId || !vendorId) {
                return { success: false, error: 'User ID and Vendor ID are required' };
            }

            // Insert or update the viewing record
            const { data, error } = await supabase
                .from('vendor_views')
                .upsert(
                    {
                        user_id: userId,
                        vendor_id: vendorId,
                        viewed_at: new Date().toISOString()
                    },
                    {
                        onConflict: 'user_id,vendor_id',
                        ignoreDuplicates: false
                    }
                )
                .select()
                .single();

            if (error) {
                console.error('Error tracking view:', error);
                return { success: false, error: error.message };
            }

            return { success: true, data };
        } catch (error) {
            console.error('Unexpected error tracking view:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Get recently viewed vendors for a user
     * @param {string} userId - The user's ID
     * @param {number} limit - Maximum number of vendors to return (default: 6)
     * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
     */
    async getRecentlyViewed(userId, limit = 6) {
        try {
            if (!userId) {
                return { success: false, error: 'User ID is required' };
            }

            const { data, error } = await supabase
                .from('vendor_views')
                .select(`
          viewed_at,
          vendor:vendor_profiles!vendor_views_vendor_id_fkey (
            id,
            business_name,
            category,
            city,
            state,
            starting_price,
            is_verified
          )
        `)
                .eq('user_id', userId)
                .order('viewed_at', { ascending: false })
                .limit(limit);

            if (error) {
                console.error('Error fetching recently viewed:', error);
                return { success: false, error: error.message };
            }

            // Transform data to match component format
            const transformedData = data?.map(item => ({
                id: item.vendor.id,
                name: item.vendor.business_name,
                category: item.vendor.vendor_category,
                location: `${item.vendor.city}, ${item.vendor.state}`,
                rating: item.vendor.average_rating || 0,
                reviewCount: item.vendor.total_reviews || 0,
                startingPrice: item.vendor.starting_price || 0,
                image: item.vendor.profile_image || 'https://images.unsplash.com/photo-1519167758481-83f29d8ae8c4?w=300&h=200&fit=crop',
                viewedAt: this.formatViewedAt(item.viewed_at),
                verified: item.vendor.is_verified || false
            })) || [];

            return { success: true, data: transformedData };
        } catch (error) {
            console.error('Unexpected error fetching recently viewed:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Clear viewing history for a user
     * @param {string} userId - The user's ID
     * @returns {Promise<{success: boolean, error?: string}>}
     */
    async clearHistory(userId) {
        try {
            if (!userId) {
                return { success: false, error: 'User ID is required' };
            }

            const { error } = await supabase
                .from('vendor_views')
                .delete()
                .eq('user_id', userId);

            if (error) {
                console.error('Error clearing history:', error);
                return { success: false, error: error.message };
            }

            return { success: true };
        } catch (error) {
            console.error('Unexpected error clearing history:', error);
            return { success: false, error: error.message };
        }
    },

    /**
     * Format timestamp to relative time
     * @param {string} timestamp - ISO timestamp
     * @returns {string} - Formatted relative time
     */
    formatViewedAt(timestamp) {
        const now = new Date();
        const viewedDate = new Date(timestamp);
        const diffMs = now - viewedDate;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
        if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
        if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} ${Math.floor(diffDays / 7) === 1 ? 'week' : 'weeks'} ago`;
        return `${Math.floor(diffDays / 30)} ${Math.floor(diffDays / 30) === 1 ? 'month' : 'months'} ago`;
    }
};

export default viewingHistoryService;
